import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ZoomIn, Plus, Minus, X } from "lucide-react";

/**
 * FontSizeControl
 *
 * Accessibility helper for users with reduced vision. A floating fixed-
 * position magnifying-glass button at the bottom-right of the viewport.
 * Tapping it expands into a small popover with `−` and `+` buttons that
 * cycle through 3 sizes: Normal (100%), Large (115%), Extra Large (135%).
 *
 * The choice persists in localStorage as `tir-font-scale` so it's set
 * once and remembered. We apply the size by setting `font-size` directly
 * on `<html>` — Tailwind's rem-based units inherit that automatically,
 * so the entire app scales without per-component plumbing.
 *
 * Mounted globally in App.js so it's visible on every coach + athlete
 * page; only the user who needs it actually clicks it.
 */

const STEPS = [
  { value: 1.0, label: "Normal" },
  { value: 1.15, label: "Large" },
  { value: 1.35, label: "Extra Large" },
];
const STORAGE_KEY = "tir-font-scale";

function readSavedScale() {
  try {
    const raw = parseFloat(localStorage.getItem(STORAGE_KEY) || "");
    if (!Number.isFinite(raw)) return 1.0;
    // Snap to the nearest preset so legacy stored values stay valid.
    let best = STEPS[0].value;
    let bestDiff = Math.abs(raw - best);
    for (const s of STEPS) {
      const d = Math.abs(raw - s.value);
      if (d < bestDiff) {
        best = s.value;
        bestDiff = d;
      }
    }
    return best;
  } catch {
    return 1.0;
  }
}

function applyScale(scale) {
  // CSS `zoom` rescales EVERYTHING (rem, px, fixed-px, absolute positions)
  // uniformly — same effect as the browser's Cmd-+/Ctrl-+ zoom, which is
  // exactly what a visually-impaired user wants. We previously set
  // `font-size: ${pct}%` on <html>, but that only scaled rem-based units,
  // leaving the dozens of arbitrary-px classes (text-[9px], w-14, etc.)
  // at their original size — inconsistent and ugly. Zoom side-steps that
  // entirely.
  //
  // Browser support: Chrome/Edge/Safari for years; Firefox 126+. Older
  // Firefox users gracefully fall back to no scaling (they can still use
  // the browser's own zoom shortcut).
  if (document.body) {
    document.body.style.zoom = String(scale);
  }
  // Clear the legacy font-size hint from the previous version of this
  // control so we don't end up double-scaling for users who stored a
  // pre-zoom preference.
  if (document.documentElement.style.fontSize) {
    document.documentElement.style.fontSize = "";
  }
}

export default function FontSizeControl() {
  const [scale, setScale] = useState(readSavedScale);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  // Chat pages pin their message input to the bottom of the viewport —
  // the FAB would sit right on top of the Send button (worst on mobile).
  const onChatPage =
    location.pathname === "/athlete/messages" ||
    /^\/coaching\/messages\/[^/]+$/.test(location.pathname);

  // Apply scale on mount + whenever it changes.
  useEffect(() => {
    applyScale(scale);
    try {
      localStorage.setItem(STORAGE_KEY, String(scale));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [scale]);

  // Click-outside / Escape closes the popover.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const idx = STEPS.findIndex((s) => Math.abs(s.value - scale) < 0.01);
  const safeIdx = idx < 0 ? 0 : idx;
  const step = STEPS[safeIdx];

  const dec = () => setScale(STEPS[Math.max(0, safeIdx - 1)].value);
  const inc = () => setScale(STEPS[Math.min(STEPS.length - 1, safeIdx + 1)].value);

  if (onChatPage) return null;

  return (
    <div
      ref={ref}
      // Sits above the platform's "Made with Emergent" badge so the two
      // FABs don't visually collide in the bottom-right corner. z-40 keeps
      // it under modals/dialogs (z-50) so it can't cover their buttons.
      className="fixed bottom-20 right-5 z-40"
      data-testid="font-size-control"
    >
      {open ? (
        <div
          className="bg-white border border-[var(--tir-border)] shadow-lg px-3 py-2 flex items-center gap-2"
          data-testid="font-size-popover"
          style={{ fontSize: "14px" }}
        >
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mr-1">
            Text size
          </div>
          <button
            type="button"
            onClick={dec}
            disabled={safeIdx === 0}
            data-testid="font-size-decrease"
            className="h-8 w-8 border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy)] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Smaller text"
          >
            <Minus size={14} />
          </button>
          <div
            className="font-mono-tir text-[12px] font-semibold text-[var(--tir-ink)] min-w-[3.25rem] text-center"
            data-testid="font-size-label"
          >
            {step.label}
          </div>
          <button
            type="button"
            onClick={inc}
            disabled={safeIdx === STEPS.length - 1}
            data-testid="font-size-increase"
            className="h-8 w-8 border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy)] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Larger text"
          >
            <Plus size={14} />
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="font-size-close"
            className="ml-1 text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            aria-label="Close text size control"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="font-size-toggle"
          // Use inline px font-size on the button itself so the icon stays a
          // consistent visual size regardless of the global font-size scale
          // the user has picked. Otherwise the FAB would balloon at 135%.
          style={{ fontSize: "14px" }}
          className="h-11 w-11 rounded-full bg-[var(--tir-navy)] text-white border border-[var(--tir-navy)] shadow-lg flex items-center justify-center hover:bg-[var(--tir-burgundy)] hover:border-[var(--tir-burgundy)] transition-colors"
          aria-label="Adjust text size"
          title="Adjust text size"
        >
          <ZoomIn size={18} />
        </button>
      )}
    </div>
  );
}
